import React from "react";
import { PlateTypeCaptureProfile, UltravioletFluorescenceCaptureProperty } from '../../../Model/CaptureProfile';
import EOpticAndCameraFeature from "../../../Model/EOpticAndCameraFeature";
import { LightPathCapability } from "../../../Model/ImagerCapabilities";
import { VisibleFluorescenceCaptureProperty } from "./../../../Model/CaptureProfile";
import DefaultImagingSettingTextBox from "./DefaultImagingSettingTextBox";
import ExposureOptions from "./ExposureOptions";

type Props = {
  style: React.CSSProperties;
  lightpathCapability: LightPathCapability;
  captureProfile: PlateTypeCaptureProfile;
  setCaptureProfile: (captureProfile: PlateTypeCaptureProfile) => void;
  onErrorOccur: (key: string) => void;
  onErrorCleared: (key: string) => void;
};

export default function FluorescenceExposureOptions({
  style,
  lightpathCapability,
  captureProfile,
  setCaptureProfile,
  onErrorOccur,
  onErrorCleared,
}: Props) {
  const fluorescenceCaptureProfile = captureProfile.fluorescence;

  if (fluorescenceCaptureProfile == null) {
    throw new Error("Fluorescence capture profile is null");
  }

  const visFluorescenceCaptureProfile = fluorescenceCaptureProfile.visibleFluorescence;
  const uvFluorescenceCaptureProfile = fluorescenceCaptureProfile.ultravioletFluorescence;

  if (visFluorescenceCaptureProfile == null) {
    throw new Error("Visible fluorescence capture profile is null");
  }

  if (uvFluorescenceCaptureProfile == null) {
    throw new Error("Ultraviolet fluorescence capture profile is null");
  }

  if (uvFluorescenceCaptureProfile.exposure == null || visFluorescenceCaptureProfile.exposure == null) {
    throw new Error("Capture profile exposure is null");
  }

  if (uvFluorescenceCaptureProfile.gain == null || visFluorescenceCaptureProfile.gain == null) {
    throw new Error("Capture profile gain is null");
  }
  if (uvFluorescenceCaptureProfile.gamma == null || visFluorescenceCaptureProfile.gamma == null) {
    throw new Error("Capture profile gamma is null");
  }

  if (visFluorescenceCaptureProfile.brightness == null) {
    throw new Error("Capture profile brightness is null");
  }

  const onVisValueChanged = (visFluorescenceCaptureProfile: VisibleFluorescenceCaptureProperty) => {
    setCaptureProfile({
      ...captureProfile,
      fluorescence: { ...fluorescenceCaptureProfile, visibleFluorescence: visFluorescenceCaptureProfile },
    });
  };

  const onUvValueChanged = (uvFluorescenceCaptureProfile: UltravioletFluorescenceCaptureProperty) => {
    setCaptureProfile({
      ...captureProfile,
      fluorescence: { ...fluorescenceCaptureProfile, ultravioletFluorescence: uvFluorescenceCaptureProfile },
    });
  };

  const gamma = lightpathCapability.camera.gamma;
  const gain = lightpathCapability.camera.gain;
  const exposure = lightpathCapability.camera.exposure;

  let gammaUvView = null;
  let gammaVisView = null;
  if (gamma != null) {
    gammaVisView = (
      <DefaultImagingSettingTextBox
        value={visFluorescenceCaptureProfile.gamma}
        onChange={(name, value) =>
          onVisValueChanged({
            ...visFluorescenceCaptureProfile,
            gamma: value,
          })
        }
        label="Gamma"
        name={EOpticAndCameraFeature.Gamma}
        unit=""
        min={gamma.minGamma}
        max={gamma.maxGamma}
        style={{
          gridArea: "visGamma",
        }}
        isInteger={false}
        disabled={false}
        onErrorOccur={(name) => onErrorOccur(name)}
        onErrorCleared={(name) => onErrorCleared(name)}
      />
    );

    gammaUvView = (
      <DefaultImagingSettingTextBox
        value={uvFluorescenceCaptureProfile.gamma}
        onChange={(name, value) =>
          onUvValueChanged({
            ...uvFluorescenceCaptureProfile,
            gamma: value,
          })
        }
        label="Gamma"
        name={EOpticAndCameraFeature.Gamma}
        unit=""
        min={gamma.minGamma}
        max={gamma.maxGamma}
        style={{
          gridArea: "uvGamma",
        }}
        isInteger={false}
        disabled={false}
        onErrorOccur={(name) => onErrorOccur(name)}
        onErrorCleared={(name) => onErrorCleared(name)}
      />
    );
  }

  let gainVisView = null;
  let gainUvView = null;
  if (gain != null) {
    gainVisView = (
      <DefaultImagingSettingTextBox
        value={visFluorescenceCaptureProfile.gain}
        onChange={(name, value) =>
          onVisValueChanged({
            ...visFluorescenceCaptureProfile,
            gain: value,
          })
        }
        label="Gain"
        name={EOpticAndCameraFeature.Gain}
        unit="db"
        min={gain.minGain}
        max={gain.maxGain}
        style={{
          gridArea: "visGain",
        }}
        isInteger={false}
        disabled={false}
        onErrorOccur={(name) => onErrorOccur(name)}
        onErrorCleared={(name) => onErrorCleared(name)}
      />
    );

    gainUvView = (
      <DefaultImagingSettingTextBox
        value={uvFluorescenceCaptureProfile.gain}
        onChange={(name, value) =>
          onUvValueChanged({
            ...uvFluorescenceCaptureProfile,
            gain: value,
          })
        }
        label="Gain"
        name={EOpticAndCameraFeature.Gain}
        unit="db"
        min={gain.minGain}
        max={gain.maxGain}
        style={{
          gridArea: "uvGain",
        }}
        isInteger={false}
        disabled={false}
        onErrorOccur={(name) => onErrorOccur(name)}
        onErrorCleared={(name) => onErrorCleared(name)}
      />
    );
  }

  let exposureUvView = null;
  if (exposure != null) {
    exposureUvView = (
      <DefaultImagingSettingTextBox
        value={uvFluorescenceCaptureProfile.exposure}
        onChange={(name, value) =>
          onUvValueChanged({
            ...uvFluorescenceCaptureProfile,
            exposure: value,
          })
        }
        label="Exposure"
        name={EOpticAndCameraFeature.Exposure}
        unit="ms"
        min={exposure.minExposure}
        max={exposure.maxExposure}
        style={{
          gridArea: "uvExposure",
        }}
        isInteger={false}
        disabled={false}
        onErrorOccur={(name) => onErrorOccur(name)}
        onErrorCleared={(name) => onErrorCleared(name)}
      />
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        ...style,
      }}
    >
      <div
        style={{
          fontWeight: "bold",
          height: "57px",
          fontSize: 13,
          lineHeight: "15px",
          display: "flex",
          alignItems: "center"
        }}
      >
        UV Fluorescence
      </div>

      <div
        style={{
          width: "100%",
          display: "grid",
          gridTemplateAreas: `'uvGamma uvGain ' 
                              'uvExposure uvBrightnes'`,
          gridTemplateColumns: "50% 50%",
          gridTemplateRows: "auto auto",
          gridRowGap: "5px",
          marginLeft: "17px",
          marginTop: "5px",
          marginBottom: "5px"
        }}
      >
        {gainUvView}
        {gammaUvView}
        {exposureUvView}
      </div>

      <div
        style={{
          fontWeight: "bold",
          height: "57px",
          fontSize: 13,
          lineHeight: "15px",
          display: "flex",
          alignItems: "center"
        }}
      >
        Visible Fluorescence
      </div>

      <div
        style={{
          width: "100%",
          display: "grid",
          gridTemplateAreas: `'visGamma visGain ' 
                              'visBrightnes visExposure'`,
          gridTemplateColumns: "50% 50%",
          gridTemplateRows: "auto auto",
          gridRowGap: "5px",
          marginLeft: "17px",
          marginTop: "5px",
        }}
      >
        {gainVisView}
        {gammaVisView}
      </div>

      <ExposureOptions
        lightpathCapability={lightpathCapability}
        exposureCaptureProperty={visFluorescenceCaptureProfile}
        onChange={onVisValueChanged}
        style={{
          width: "100%",
        }}
        onErrorCleared={onErrorCleared}
        onErrorOccur={onErrorOccur}
      />
    </div>
  );
}
